import { useEffect, useRef, useState } from "react";
import { ApiError, discordBindPoll, discordBindRefresh, discordBindStart, type DiscordBindSession } from "../lib/api";
import { useT } from "../lib/i18n";
import { DiscordMark, Spinner } from "./shell/AuthLayout";

/** 状态多久查一次：与 notify-hub 自己的绑定页一致 */
const POLL_MS = 2000;

/** 粘贴快捷键按系统给：Mac 是 ⌘V，其余 Ctrl+V */
const PASTE_KEY = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ + V" : "Ctrl + V";

/** 服务端回参数错（100）= 这个会话码 notify-hub 已经不认了（过了短链有效期等），只能重开一个会话 */
const sessionGone = (e: unknown) => e instanceof ApiError && e.code === "100";

/**
 * Discord 绑定的界面，我们自己画（替代 notify-hub 的 iframe：那页是繁体、写给管理员看的五步，用户看了就不想弄）。
 *
 * 数据全走我们的服务端（admin/index.ts 的 /discord/bind/session），服务端代调 notify-hub。
 * 用户只做一件事：点按钮（复制命令 + 打开 Discord），到 Discord 里粘贴发给机器人；绑上之后 onBound 被调用，由调用方决定接下来去哪。
 */
export function DiscordBindFlow({ onBound }: { onBound: (discordName: string) => void }) {
  const t = useT();
  const [s, setS] = useState<DiscordBindSession | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  /** 复制成功过一次就一直亮着红字提示：有人看到「等你发送」以为是页面在加载，不知道下一步在 Discord 里粘贴 */
  const [pasteNudge, setPasteNudge] = useState(false);
  /** 复制失败才把命令亮出来让人照着输；平时不展示，用户只管点按钮、去 Discord 粘贴 */
  const [copyFailed, setCopyFailed] = useState(false);
  const boundOnce = useRef(false);

  const take = (next: DiscordBindSession) => {
    setS(next);
    if (next.status === "bound" && !boundOnce.current) { boundOnce.current = true; onBound(next.discord_username); }
  };
  const run = async (f: () => Promise<DiscordBindSession>) => {
    setBusy(true); setErr(null);
    try { take(await f()); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  useEffect(() => { void run(discordBindStart); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** 会话没了就静默重开，用户不用管；轮询每 2 秒一轮，重开期间不重复发起 */
  const restarting = useRef(false);
  const restart = () => {
    if (restarting.current) return;
    restarting.current = true;
    void run(discordBindStart).finally(() => { restarting.current = false; });
  };

  // 等待中才轮询；绑上 / 过期就停。一次失败不打断，下一轮再试（网络抖一下不该把人赶出流程）
  useEffect(() => {
    if (!s || s.status !== "pending") return;
    const id = setInterval(() => {
      discordBindPoll(s.session).then(take).catch((e) => { if (sessionGone(e)) restart(); /* 其余下一轮再查 */ });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [s?.session, s?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * 码不展示，过期也不该让用户操心：过期了、或离过期不到 1 分钟（免得刚复制就失效），静默换一个新码。
   * 换码用同一个会话，轮询照常；复制过旧码的人再点一次按钮就是新码。
   */
  useEffect(() => {
    if (!s || s.status === "bound") return;
    const wait = s.status === "expired" ? 0 : Math.max(0, s.expires_at * 1000 - Date.now() - 60_000);
    const id = setTimeout(() => {
      void run(() => discordBindRefresh(s.session).catch((e) => { if (sessionGone(e)) return discordBindStart(); throw e; }));
    }, wait);
    return () => clearTimeout(id);
  }, [s?.session, s?.status, s?.expires_at]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * 一个按钮做两件事：复制命令、打开 Discord。**先发起复制、不等它**再同步 window.open——
   * 等复制完成再开窗口，点击手势已经过了，浏览器会把新窗口当弹窗拦掉；
   * 反过来先开窗口，本页失焦，剪贴板写入会被拒（Document is not focused）。
   */
  const copyAndOpen = () => {
    if (!s) return;
    navigator.clipboard?.writeText(s.command)
      .then(() => { setCopied(true); setPasteNudge(true); setCopyFailed(false); setTimeout(() => setCopied(false), 2500); })
      .catch(() => setCopyFailed(true));
    if (!navigator.clipboard) setCopyFailed(true);
    window.open(s.invite_url, "_blank", "noopener");
  };

  if (!s) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-3 text-sm text-ink-placeholder">
        {busy ? <Spinner className="h-6 w-6" /> : (
          <>
            {err && <p className="text-danger-800">{err}</p>}
            <button type="button" onClick={() => void run(discordBindStart)} className="text-primary hover:underline">{t("common.retry")}</button>
          </>
        )}
      </div>
    );
  }

  if (s.status === "bound") {
    return (
      <div className="flex flex-col items-center rounded-lg bg-success-50 px-4 py-8 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-success text-xl text-white">✓</span>
        <p className="mt-3 text-base font-medium text-ink">{t("bindf.bound")}</p>
        {s.discord_username && <p className="mt-1 text-sm text-ink-secondary">{t("bindf.boundAs", { name: s.discord_username })}</p>}
      </div>
    );
  }

  /*
   * 越简单越好（leo 2026-10-19）：一个主按钮「复制命令并打开 Discord」，到 Discord 里粘贴发给机器人就完事。
   * 命令、倒计时、换码都不展示：码过期由上面的定时器静默换；只有复制失败时才亮出命令让人照着输。
   */
  return (
    <div>
      <button type="button" onClick={copyAndOpen}
        className="flex h-11 w-full items-center justify-center gap-2.5 rounded-lg bg-[#5865F2] text-[15px] font-medium text-white shadow-light transition-colors hover:bg-[#4752C4]">
        <DiscordMark />{copied ? t("bindf.copiedOpen") : t("bindf.copyAndOpen")}
      </button>
      <p className="mt-3 text-sm leading-relaxed text-ink-secondary">{t("bindf.pasteHint")}</p>

      {pasteNudge && !copyFailed && (
        <div className="mt-4 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2.5">
          <p className="text-sm font-semibold text-danger-800">{t("bindf.copiedTitle")}</p>
          <p className="mt-1 text-sm leading-relaxed text-danger-800">{t("bindf.pasteNow", { key: PASTE_KEY })}</p>
        </div>
      )}

      {copyFailed && (
        <div className="mt-4 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2">
          <p className="text-xs text-warning-800">{t("bindf.copyFailed")}</p>
          <code className="mt-1 block select-all font-mono text-base tracking-wide text-ink">{s.command}</code>
        </div>
      )}

      {err && <p className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-800">{err}</p>}
      <p className="mt-4 flex items-center gap-2 text-sm text-ink-secondary">
        <Spinner className="h-3.5 w-3.5" />{t("bindf.waiting")}
      </p>
    </div>
  );
}
